/**
 * The picker's year control. `/season/start-options` answers provider
 * readiness for exactly one year, so `minYear` and `maxYear` are the same
 * value today and both arrows render disabled (D2, `plan.md` `## 2`). The
 * props still carry independent bounds so widening this later is a props
 * change, not a rewrite.
 *
 * Local to this feature rather than a `@sportsmet/ui` export: one consumer
 * today, following `PickRequirementStepper.tsx`'s precedent.
 */
import { Button } from "@sportsmet/ui";
import type { JSX } from "react";

import { PICKER_YEAR_LABEL } from "./seasonCardModel";

export type typeSeasonYearStepperProps = {
  value: number;
  minYear: number;
  maxYear: number;
  onChange: (next: number) => void;
  disabled?: boolean | undefined;
};

export function SeasonYearStepper(
  props: typeSeasonYearStepperProps,
): JSX.Element {
  const { value, minYear, maxYear, onChange, disabled = false } = props;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-body-strong text-ink">{PICKER_YEAR_LABEL}</span>
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          size="icon"
          aria-label="Earlier year"
          data-testid="season-year-decrement"
          disabled={disabled || value <= minYear}
          onClick={() => {
            onChange(value - 1);
          }}
        >
          -
        </Button>
        <span
          data-testid="season-year-value"
          className="min-w-8 text-center text-body-strong text-ink"
        >
          {value}
        </span>
        <Button
          variant="secondary"
          size="icon"
          aria-label="Later year"
          data-testid="season-year-increment"
          disabled={disabled || value >= maxYear}
          onClick={() => {
            onChange(value + 1);
          }}
        >
          +
        </Button>
      </div>
    </div>
  );
}
